import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import FacultyLayout from './FacultyLayout';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
} from 'lucide-react';
import { facultyAPI } from '../../services/api';

interface Submission {
  id: number;
  title: string;
  category_name: string;
  status: string;
  submitted_date: string;
  feedback?: string;
}

interface FacultyStats {
  total_submissions: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
}

export default function FacultyDashboard() {
  const { user } = useContext(AuthContext);
  const [statsData, setStatsData] = useState<FacultyStats>({
    total_submissions: 0,
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0,
  });
  const [recentSubmissions, setRecentSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const result = await facultyAPI.getStats();
        if (!result.error && result.data) {
          // Handle multi-query result structure
          const data = result.data;
          if (Array.isArray(data) && data.length >= 2) {
            setStatsData(prev => data[0][0] || prev);
            setRecentSubmissions(data[1] || []);
          } else if (data.stats) {
            setStatsData(data.stats);
            setRecentSubmissions(data.recent || []);
          }
        }
      } catch (err) {
        console.error('Failed to fetch faculty stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const stats = [
    {
      label: 'Total Submissions',
      value: statsData.total_submissions.toString(),
      icon: FileText,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      label: 'Pending Review',
      value: statsData.pending_count.toString(),
      icon: Clock,
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
    },
    {
      label: 'Approved',
      value: statsData.approved_count.toString(),
      icon: CheckCircle,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
    },
    {
      label: 'Rejected',
      value: statsData.rejected_count.toString(),
      icon: XCircle,
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
    },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-4 h-4 mr-1" />
            Approved
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
            <Clock className="w-4 h-4 mr-1" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <FacultyLayout>
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">Welcome , {user?.name}!</h1>
              <p className="text-purple-100 text-lg">{user?.department}</p>
              <p className="text-purple-100 mt-2">Ready to share your creative work today?</p>
            </div>
            <div className="mt-6 md:mt-0">
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.textColor}`} />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Recent Submissions */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Submissions</h2>
                <p className="text-gray-600 mt-1">Your latest creative works</p>
              </div>
              <Link
                to="/faculty/submissions"
                className="text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                View All →
              </Link>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-6 text-center text-gray-500">Loading...</div>
            ) : recentSubmissions.length > 0 ? (
              recentSubmissions.map((submission) => (
                <Link
                  key={submission.id}
                  to={`/faculty/submissions/${submission.id}`}
                  className="block p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{submission.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>{submission.category_name}</span>
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(submission.submitted_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">{getStatusBadge(submission.status)}</div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500">No submissions yet</div>
            )}
          </div>
        </div>

        {/* Pro Tip */}
        <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-6 text-white">
          <h3 className="font-semibold mb-2">Pro Tip</h3>
          <p className="text-purple-100 mb-4">
            Add detailed descriptions to your submissions to help reviewers understand the
            context and inspiration behind your creative work.
          </p>
          <Link
            to="/faculty/submit"
            className="inline-flex items-center text-white hover:text-purple-100 font-medium"
          >
            Submit Work Now →
          </Link>
        </div>
      </div>
    </FacultyLayout>
  );
}
